import { useEffect, useRef } from "react";
import { Check, CircleAlert, Loader2 } from "lucide-react";
import { Markdown } from "@/components/ai/Markdown";
import { ArtifactCard } from "@/components/ai/Artifacts";
import { LensMark } from "@/components/brand/ProximaLogo";
import { cn } from "@/lib/utils";

const clean = (s = "") => s.replace(/…$/, "");

/** Which data the answer is built on. A quiet list while fetching, one line when done. */
export const ToolSteps = ({ tools = [] }) => {
  if (!tools.length) return null;
  const allDone = tools.every((t) => t.done);
  if (allDone) {
    return (
      <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[13px] text-muted-foreground" data-testid="ai-tool-steps">
        <Check className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden="true" />
        <span>Bygger på</span>
        {tools.map((t, i) => (
          <span key={t.call_id} className={cn(t.ok === false ? "text-amber-700 dark:text-amber-300" : "text-foreground")} data-testid="ai-tool-step">
            {clean(t.label) || t.name}
            {i < tools.length - 1 ? <span className="text-muted-foreground"> ·</span> : null}
          </span>
        ))}
      </div>
    );
  }
  return (
    <ul className="space-y-1.5" data-testid="ai-tool-steps">
      {tools.map((t) => (
        <li key={t.call_id} className="flex items-center gap-2 text-[13px]" data-testid="ai-tool-step">
          {t.done ? t.ok === false ? <CircleAlert className="h-3.5 w-3.5 shrink-0 text-amber-600" aria-hidden="true" /> : <Check className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden="true" /> : <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-muted-foreground" aria-hidden="true" />}
          <span className={cn("truncate", t.done ? "text-foreground" : "text-muted-foreground")}>{clean(t.label) || t.name}</span>
        </li>
      ))}
    </ul>
  );
};

export const MessageBlock = ({ message, onPin, pinnedIds = [], compact = false }) => {
  const m = message;
  if (m.role === "user") {
    return (
      <div className="flex justify-end py-3" data-testid="ai-message-block-user">
        <p className="max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-br-md bg-muted px-4 py-2.5 text-[15px] leading-relaxed text-foreground">{m.content}</p>
      </div>
    );
  }
  const showArtifacts = m.artifacts?.length > 0;
  const thinking = m.streaming && !m.content && !m.tools?.length;
  return (
    <article className={cn("grid gap-x-3 py-3", compact ? "grid-cols-1" : "grid-cols-[28px_minmax(0,1fr)]")} data-testid="ai-message-block">
      {!compact ? <LensMark className="mt-[3px] h-5 w-5" /> : null}
      <div className="min-w-0 space-y-4">
        <ToolSteps tools={m.tools} />
        {showArtifacts ? (
          <div className="space-y-3">
            {m.artifacts.map((a) => (
              <ArtifactCard key={a.id} artifact={a} onPin={onPin} pinned={pinnedIds.includes(a.id)} compact={compact} />
            ))}
          </div>
        ) : null}
        {m.content ? (
          <div className="relative">
            <Markdown>{m.content}</Markdown>
            {m.streaming ? <span className="caret-blink ml-0.5 inline-block h-[1.1em] w-[2px] translate-y-[3px] bg-primary" aria-hidden="true" /> : null}
          </div>
        ) : thinking ? (
          <div className="flex items-center gap-2 text-[14px] text-muted-foreground" data-testid="ai-thinking">
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> Tenker …
          </div>
        ) : null}
        {m.error ? (
          <div className="flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-[13.5px] text-rose-800 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-200" data-testid="ai-message-error">
            <CircleAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" /> {m.error}
          </div>
        ) : null}
      </div>
    </article>
  );
};

/** List of message blocks; auto-follows while streaming. */
export const Thread = ({ messages = [], onPin, pinnedIds, compact = false, className, children }) => {
  const endRef = useRef(null);
  const last = messages[messages.length - 1];
  useEffect(() => {
    endRef.current?.scrollIntoView({ block: "end", behavior: "smooth" });
  }, [messages.length, last?.content?.length, last?.artifacts?.length, last?.tools?.length]);
  return (
    <div className={cn("space-y-1", className)} data-testid="ai-thread">
      {children}
      {messages.map((m) => (
        <MessageBlock key={m.id} message={m} onPin={onPin} pinnedIds={pinnedIds} compact={compact} />
      ))}
      <div ref={endRef} />
    </div>
  );
};
